<template>
  <BaseModule
    v-bind="$props"
    class="container hotels-list"
    data-module="hotels-list"
    :style="{
      '--marker-color': markerColor,
    }"
  >
    <div class="hotels">
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
          :style="{ color: subtitleColor }"
        >
          {{ subtitle }}
        </h3>
      </header>

      <Loader v-if="loading" />

      <div
        v-else
        class="contents"
      >
        <div
          v-if="showFilter"
          class="hotels__controls"
        >
          <v-select
            id="hotel-field"
            v-model="selectedHotel"
            class="control-item"
            name="hotel-field"
            label="Nome"
            :placeholder="$t('searchByHotelName')"
            :options="hotelsListOptions"
            :clearable="true"
            :reduce="(opt) => opt.Codigo"
          >
            <template #no-options>Nenhum resultado encontrado</template>
          </v-select>
          <v-select
            id="destination-field"
            v-model="selectedDestination"
            class="control-item"
            name="destination-field"
            label="Nome"
            :placeholder="$t('searchByDestination')"
            :options="destiniesListOptions"
            :clearable="true"
            :reduce="(opt) => opt.Codigo"
          >
            <template #no-options>Nenhum resultado encontrado</template>
          </v-select>
          <button
            class="control-item open-map"
            @click="handleOpenModal"
          >
            <IconMap />
            <span>{{ $t('viewOnMap') }}</span>
          </button>
        </div>

        <ul
          v-if="hasHotels"
          class="hotels__list"
          :style="{ '--columns-on-desktop': columnsOnDesktop }"
        >
          <li
            v-for="(h, index) in hotelsFilterList"
            v-show="index < visibleHotelsNumber"
            :key="index"
            :data-hotel="h.Codigo"
            class="hotels__list__item"
          >
            <EditorLink
              class="bloco-hotel"
              :model="hotelLinkModel(h)"
              :style="{ backgroundImage: getHotelBgImage(h) }"
              @click.native="trackCustomEvent(h)"
            >
              <p
                v-if="h.MenorTarifa.TemDesconto"
                class="promo"
              >
                {{ $tc('discount', h.MenorTarifa.PercentualDesconto) }}
              </p>

              <div class="infos">
                <div class="infos-destination">
                  <p class="cidade">
                    {{ h.Destino.Cidade }}
                  </p>
                  <h4 class="nome">
                    {{ h.Nome }}
                  </h4>
                </div>

                <div class="infos-content">
                  <div
                    v-if="h.MenorTarifa.Valor && showValor"
                    class="infos-wrapper"
                  >
                    <p class="diaria">
                      {{ $t('dailyRate') }}
                      <span
                        v-if="h.MenorTarifa.TemDesconto"
                        class="valor-promo"
                      >
                        {{ h.MenorTarifa.ValorSemDescontoFormatado }}
                      </span>
                      <span
                        class="valor"
                        :class="{ 'has-discount': h.MenorTarifa.TemDesconto }"
                      >
                        {{ h.MenorTarifa.ValorFormatado }}
                        <span class="slb-tooltip">
                          {{ getDisclaimer(h) }}
                        </span>
                      </span>
                    </p>
                  </div>

                  <span class="cta">
                    {{ getCTALabel(h) }}
                  </span>
                </div>
              </div>
            </EditorLink>
          </li>
        </ul>
        <button
          v-if="hasHotelsToLoad"
          class="cta vazado cta--ver-mais"
          @click="showMoreHotels()"
        >
          {{ $t('seeMore') }}
        </button>

        <div
          v-if="!hasHotels"
          class="content no-hotels"
        >
          <span class="i-glass"></span>
          <h3 class="tit">
            {{ $t('noResults') }}
          </h3>
          <p class="no-hotels__message">
            {{ $t('noResultsSubtitle') }}
          </p>
        </div>
      </div>
    </div>

    <template slot="modal">
      <div
        v-if="isMapOpen"
        class="map-modal"
      >
        <div
          class="close-btn"
          :title="$t('mapViewClose')"
          @click="handleCloseModal"
        >
          <IconClose size="xs" />
          <span>{{ $t('mapViewClose') }}</span>
        </div>
        <div
          v-if="!isMobile"
          class="map-hotels custom-scrollbar"
        >
          <div
            class="map-hotels__header"
            v-html="$t('mapViewTitle')"
          ></div>
          <ul class="map-hotels__list">
            <li
              v-for="(h, index) in hotelsInMap"
              :key="index"
              :data-hotel="h.Codigo"
              class="hotel-card"
              :style="{ backgroundImage: getHotelBgImage(h) }"
              :on-focus="hotelOnFocus === h"
              @mouseover="hotelOnFocus = h"
              @mouseleave="hotelOnFocus = null"
              @click="zoomOnHotel(h)"
            >
              <div class="hotel-card__content">
                <div class="hotel-card__name">{{ h.Nome }}</div>
                <div
                  class="hotel-card__cta"
                  :class="{
                    'hotel-card__cta--no-price':
                      !h.MenorTarifa.Valor || !showValor,
                  }"
                  @click="handleMarkerClick(h)"
                >
                  <span
                    v-if="h.MenorTarifa.Valor && showValor"
                    class="hotel-card__price"
                  >
                    {{ $t('priceFrom') }}
                    <strong>{{ h.MenorTarifa.ValorFormatado }}</strong>
                  </span>
                  <IconArrow direction="right" />
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="map-container">
          <l-map
            ref="map"
            @update:bounds="handleUpdate"
          >
            <l-tile-layer
              :url="url"
              :attribution="attribution"
            />
            <l-marker
              v-for="(h, idx) in validHotels"
              :key="idx"
              :lat-lng="[h.Latitude, h.Longitude]"
              class="pin"
              :z-index-offset="hotelOnFocus === h ? 1000 : 0"
              :options="{ riseOnHover: true }"
              @click="handleMarkerClick(h)"
            >
              <l-icon
                v-if="h.MenorTarifa.Valor && showValor"
                :icon-size="[80, 24]"
                :icon-anchor="[40, 30]"
              >
                <div
                  class="pin__tag"
                  :class="{ 'pin__tag--selected': hotelOnFocus === h }"
                >
                  {{ h.MenorTarifa.ValorFormatado }}
                </div>
              </l-icon>
              <l-icon
                v-else
                :icon-size="[40, 40]"
                :icon-anchor="[20, 38]"
              >
                <IconPinRoom
                  class="pin__icon"
                  :class="{ 'pin__icon--selected': hotelOnFocus === h }"
                  style="--size: 40px"
                />
              </l-icon>
            </l-marker>
          </l-map>

          <div class="map-search">
            <v-select
              id="destination-field-map"
              v-model="selectedDestination"
              class="control-item"
              name="destination-field-map"
              label="Nome"
              :placeholder="$t('searchByDestination')"
              :options="destiniesListOptions"
              :clearable="true"
              :reduce="(opt) => opt.Codigo"
            >
              <template #no-options>Nenhum resultado encontrado</template>
            </v-select>
          </div>

          <EditorLink
            v-if="hotelOnFocus && isMobile"
            :model="hotelLinkModel(hotelOnFocus)"
            class="hotel-card hotel-card--floating"
            :style="{ backgroundImage: getHotelBgImage(hotelOnFocus) }"
            @click.native.stop="handleLinkClick(hotelOnFocus)"
          >
            <div class="hotel-card__content">
              <div class="hotel-card__name">{{ hotelOnFocus.Nome }}</div>
              <span
                class="hotel-card__cta"
                :class="{
                  'hotel-card__cta--no-price':
                    !hotelOnFocus.MenorTarifa.Valor || !showValor,
                }"
              >
                <span
                  v-if="hotelOnFocus.MenorTarifa.Valor && showValor"
                  class="hotel-card__price"
                >
                  {{ $t('priceFrom') }}
                  <strong>{{ hotelOnFocus.MenorTarifa.ValorFormatado }}</strong>
                </span>
                <IconArrow direction="right" />
              </span>
            </div>
          </EditorLink>
        </div>
      </div>
    </template>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';
import { LMap, LTileLayer, LMarker, LTooltip, LIcon } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import debounce from 'lodash/debounce';

import getApiHotels from '@/mixins/getApiHotels';
import base from '@/mixins/base';
import { pushDataLayer } from '@/helpers/gtm';
import isObject from '@/helpers/isObject';
import randomizeArray from '@/helpers/randomizeArray';
import Loader from '@/components/Loader.vue';
import IconMap from '@/components/icons/IconMap.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconPinRoom from '@/components/icons/IconPinRoom.vue';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

export default {
  name: 'HotelsList',
  components: {
    Loader,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon,
    IconMap,
    IconClose,
    IconArrow,
    IconPinRoom,
    BaseModule,
    EditorLink,
    'v-select': vSelect,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    showFilter: {
      type: Boolean,
      default: true,
    },
    titleColor: {
      type: String,
      default: '',
    },
    subtitleColor: {
      type: String,
      default: '',
    },
    markerColor: {
      type: String,
      default: '',
    },
    hotelsLinks: {
      type: Array,
      default() {
        return [];
      },
    },
    selectLandingPageId: {
      type: Number,
      default: null,
    },
    randomizeList: {
      type: Boolean,
      default: false,
    },
    showValor: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: true,
      selectedHotel: '',
      selectedDestination: '',
      visibleHotelsNumber: 6,
      isMapOpen: false,
      bounds: [],
      fitBoundsOpts: {
        maxZoom: 15,
        paddingTopLeft: [0, 50], // 50px de padding top para evitar da busca sobrepor o pin
      },
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      map: null,
      hotelOnFocus: null,
      isMobile: window.innerWidth < 992,
    };
  },
  computed: {
    hasHotels() {
      return this.hotelsFilterList.length > 0;
    },
    hotelsListOptions() {
      const filteredHotels = this.validHotels.filter((hotel) => {
        if (this.selectedDestination) {
          return hotel.Destino.CodigoCidade === this.selectedDestination;
        }
        return true;
      });
      return filteredHotels;
    },
    hotelsFilterList() {
      const hotelsList = this.randomizeList
        ? this.randomize(this.validHotels)
        : this.validHotels;

      return hotelsList.filter((hotel) => {
        const destinationMatches = this.selectedDestination
          ? hotel.Destino.CodigoCidade === this.selectedDestination
          : true;
        const hotelMatches = this.selectedHotel
          ? hotel.Codigo === this.selectedHotel
          : true;

        return destinationMatches && hotelMatches;
      });
    },
    hasHotelsToLoad() {
      return this.visibleHotelsNumber < this.hotelsFilterList.length;
    },
    validHotels() {
      const validHotels = this.hotels.filter(
        (hotel) => hotel.Latitude && hotel.Longitude,
      );
      const ignoredCount = this.hotels.length - validHotels.length;
      if (ignoredCount > 0) {
        console.warn(`${ignoredCount} hotéis não exibidos`);
      }

      return validHotels.map((hotel) => ({
        ...hotel,
        Latitude: Number(hotel.Latitude.replace(',', '.')),
        Longitude: Number(hotel.Longitude.replace(',', '.')),
      }));
    },
    hotelsInMap() {
      return this.validHotels.filter((hotel) => {
        return this.bounds.contains
          ? this.bounds.contains([hotel.Latitude, hotel.Longitude])
          : true; // avoid displaying no hotel before interacting with map
      });
    },
    destiniesListOptions() {
      const cities = [];
      const destinations = [];

      this.validHotels.forEach((hotel) => {
        if (!cities.includes(hotel.Destino.CodigoCidade)) {
          cities.push(hotel.Destino.CodigoCidade);
          destinations.push({
            Codigo: hotel.Destino.CodigoCidade,
            Nome: hotel.Destino.Cidade,
          });
        }
      });

      destinations.sort((destA, destB) => {
        return destA.Nome.localeCompare(destB.Nome);
      });

      return destinations;
    },
    columnsOnDesktop() {
      const hotelCount = this.hotelsFilterList.length;

      if (hotelCount > 6) {
        return 3;
      }

      // 2 colunas quando número de hotéis for 2, 4 ou 3n+1, senão 3 colunas
      return hotelCount === 2 || hotelCount === 4 || hotelCount % 3 === 1
        ? 2
        : 3;
    },
    ...mapGetters(['settings']),
  },
  watch: {
    selectLandingPageId() {
      this.requestHotels();
    },
    selectedHotel() {
      this.visibleHotelsNumber = 6;
    },
    selectedDestination() {
      this.selectedHotel = '';
      this.visibleHotelsNumber = 6;

      const visiblePins = this.getPins();

      if (this.map && visiblePins.length > 0) {
        // ref https://leafletjs.com/reference.html#map-fitbounds
        this.map.fitBounds(visiblePins, this.fitBoundsOpts);

        if (this.hotelsFilterList.length === 1) {
          // eslint-disable-next-line prefer-destructuring
          this.hotelOnFocus = this.hotelsFilterList[0];
        } else {
          this.hotelOnFocus = null;
        }
      }
    },
  },
  mounted() {
    this.requestHotels();

    window.addEventListener('resize', debounce(this.onResize, 150));
  },
  methods: {
    trackCustomEvent(hotel) {
      const hotelDiscount = hotel.MenorTarifa.PercentualDesconto;
      const hotelPrice = hotel.MenorTarifa.Valor;

      pushDataLayer({
        event: 'select_hotel',
        destination: hotel.Destino.Cidade,
        hotel_id: String(hotel.Codigo),
        hotel_name: hotel.Nome,
        percentage_discount: hotelDiscount > 0 ? `${hotelDiscount}%` : null,
        price_starting: hotelPrice > 0 ? hotelPrice : null,
      });
    },
    getDisclaimer(hotel) {
      const adultos = hotel.MenorTarifa.NumeroAdultos;
      const noites = hotel.MenorTarifa.NumeroNoites;

      return this.$t('disclaimer', {
        nights: this.$tc('night', noites),
        adults: this.$tc('adult', adultos),
      });
    },
    getValueColor(hotel) {
      return hotel.MenorTarifa.TemDesconto
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    showMoreHotels() {
      this.visibleHotelsNumber += 6;
    },
    getHotelLink(hotel) {
      return this.hotelsLinks.find((link) => link.hotel === hotel.Codigo);
    },
    hotelLinkModel(hotel) {
      const hotelLink = this.getHotelLink(hotel);

      if (isObject(hotelLink) && hotelLink.linkUrl?.value) {
        return hotelLink.linkUrl;
      }

      const letsbookUrl = this.settings.letsbookUrl.replace(
        /\/admin\/?$/i,
        `/reserva/busca?hotel=${hotel.Codigo}`,
      );

      return {
        openInNewTab: true,
        type: 'externalLink',
        value: letsbookUrl,
      };
    },
    getHotelBgImage(hotel) {
      return hotel.Imagens.ImagemPrincipal
        ? `url('${hotel.Imagens.ImagemPrincipal}')`
        : '';
    },
    getCTALabel(hotel) {
      const { value: link } = this.hotelLinkModel(hotel);

      return link.includes('letsbook') ? this.$t('book') : this.$t('seeHotel');
    },
    async requestHotels() {
      this.loading = true;

      const letsbookLandingPageId =
        this.selectLandingPageId || this.settings.letsbookLandingPageId;
      const url = `${this.settings.letsbookApiId}/hoteisLanding/${letsbookLandingPageId}/${this.apiLocale}`;

      try {
        await this.getApiHotels({
          customEndpoint: url,
          customBehavior: this.randomize,
        });
      } catch (err) {
        console.warn(err);
      }

      this.loading = false;
    },
    randomize(hotelArray) {
      if (this.randomizeList) {
        return randomizeArray(hotelArray);
      }

      return hotelArray;
    },
    handleOpenModal() {
      this.isMapOpen = true;
      document.querySelector('html').classList.add('scroll-block');

      this.$nextTick(() => {
        const pins = this.getPins();
        this.map = this.$refs.map.mapObject;

        // ref https://leafletjs.com/reference.html#map-fitbounds
        this.map.fitBounds(pins, this.fitBoundsOpts);
      });
    },
    handleCloseModal() {
      this.isMapOpen = false;
      document.querySelector('html').classList.remove('scroll-block');
      this.map = null;
    },
    handleUpdate(newBounds) {
      this.bounds = newBounds;
    },
    getMarkerIcon(color) {
      const fill = color ? color.replace('#', '%23') : '%233e9aa6';
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='42' height='70' viewBox='0 0 42 70' fill='none'%3E%3Cpath d='M31.5 23.3333C31.5 20.1129 30.4746 17.3633 28.4239 15.0846C26.373 12.806 23.8985 11.6667 21 11.6667C18.1015 11.6667 15.627 12.806 13.5762 15.0846C11.5254 17.3633 10.5 20.1129 10.5 23.3333C10.5 26.5538 11.5254 29.3034 13.5762 31.5821C15.627 33.8607 18.1015 35 21 35C23.8985 35 26.373 33.8607 28.4239 31.5821C30.4746 29.3034 31.5 26.5538 31.5 23.3333ZM42 23.3333C42 26.645 41.5489 29.3641 40.6465 31.4909L25.7168 66.7643C25.2793 67.767 24.6299 68.5568 23.7686 69.1341C22.9073 69.7114 21.9844 70 21 70C20.0156 70 19.0927 69.7114 18.2314 69.1341C17.3701 68.5568 16.7344 67.767 16.3242 66.7643L1.35352 31.4909C0.451172 29.3641 0 26.645 0 23.3333C0 16.8924 2.05078 11.3932 6.15235 6.83594C10.2539 2.27864 15.2031 0 21 0C26.7969 0 31.7461 2.27864 35.8476 6.83594C39.9492 11.3932 42 16.8924 42 23.3333Z' fill='${fill}'/%3E%3C/svg%3E`;
    },
    getPins() {
      const pins = this.hotelsFilterList.map((hotel) => [
        hotel.Latitude,
        hotel.Longitude,
      ]);

      return pins;
    },
    handleLinkClick(hotel) {
      const model = this.hotelLinkModel(hotel);

      if (model.type === 'page') {
        this.handleCloseModal();
      }
    },
    handleMarkerClick(hotel) {
      if (this.isMobile) {
        this.hotelOnFocus = hotel;
      } else {
        const link = this.hotelLinkModel(hotel);

        if (link.type !== 'page') {
          window.open(link.value, link.openInNewTab ? '_blank' : '_self');
        }
      }
    },
    onResize() {
      const isMobile = window.innerWidth < 992; // lg breakpoint

      if (this.isMobile !== isMobile) {
        this.isMobile = isMobile;
      }
    },
    zoomOnHotel(hotel) {
      if (this.map) {
        this.map.fitBounds(
          [[hotel.Latitude, hotel.Longitude]],
          this.fitBoundsOpts,
        );
      }
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            label: 'Configurações',
            helpText:
              'Caso algum dos hotéis não esteja visível na listagem,<br> entre em contato com atendimento.',
            fields: [
              {
                name: 'selectLandingPageId',
                type: 'SelectLandingPageId',
                label: 'Defina o agrupador de hotéis',
              },
              {
                name: 'showFilter',
                type: 'Checkbox',
                label: 'Mostrar filtro',
              },
              {
                name: 'randomizeList',
                type: 'Checkbox',
                label: 'Listar hotéis em ordem aleatória',
              },
              {
                name: 'showValor',
                type: 'Checkbox',
                label: 'Mostrar preços',
                default: true,
              },
            ],
          },
          {
            name: 'hotelsLink',
            label: 'Alterar link padrão dos hotéis',
            helpText:
              'Por padrão direcionamos os hotéis para o Motor de reservas,<br> mas você pode direcionar para outras páginas.',
            fields: [
              {
                name: 'hotelsLinks',
                type: 'List',
                itemLabel({ hotel: hotelId }) {
                  if (!hotelId) {
                    return '<span style="color:red">Hotel não selecionado</span>';
                  }

                  if (this.loading) {
                    return `Hotel ID ${hotelId}`;
                  }

                  const thisHotel = this.hotels.find(
                    (hotel) => hotel.Codigo === hotelId,
                  );

                  return thisHotel
                    ? thisHotel.Nome
                    : '<span style="color:red">Hotel selecionado não encontrado</span>';
                },
                addItemLabel: 'Adicionar link de hotel',
                fields: [
                  {
                    name: 'hotel',
                    type: 'VueSelect',
                    label: 'Selecionar hotel',
                    options() {
                      const hotelsToOptions = (hotels) =>
                        hotels.map((hotel) => ({
                          label: hotel.Nome,
                          value: hotel.Codigo,
                        }));

                      // Are hotels already loaded?
                      if (this.hotels.length > 0) {
                        return hotelsToOptions(this.hotels);
                      }

                      // THIS IS PURE MAGIC
                      return new Promise((resolve) => {
                        const unwatch = this.$watch('hotels', (hotels) => {
                          resolve(hotelsToOptions(hotels));
                          unwatch();
                        });
                      });
                    },
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                    availableTypes: ['externalLink', 'page'],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'subtitleColor', // migrate this
                after: 'titleColor',
                type: 'ColorPicker',
                label: 'Cor do subtítulo',
              },
              {
                name: 'markerColor',
                type: 'ColorPicker',
                label: 'Cor do marcador no mapa',
              },
            ],
            toRemove: ['textColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        discount: 'Até {count}% OFF',
        dailyRate: 'a partir de',
        priceFrom: 'Acomodações a partir de',
        adult: '1 adulto | {count} adultos',
        night: '1 diária | {count} diárias',
        disclaimer:
          'Valor da diária baseado em uma pesquisa de 7 diárias para 1 adulto',
        seeHotel: 'Ver mais',
        seeMore: 'Ver mais',
        book: 'Reservar',
        noResults: 'Não encontramos resultados para os filtros selecionados.',
        noResultsSubtitle:
          'Experimente expandir seus critérios de busca, temos ótimas opções de hotéis para você!',
        searchByHotelName: 'Buscar por nome do hotel',
        searchByDestination: 'Buscar por destino',
        mapViewTitle: 'Selecione o hotel na lista <br/> ou pelo ícone no mapa',
        mapViewClose: 'Fechar mapa',
        viewOnMap: 'Ver no mapa',
      },
      en: {
        discount: 'Up to {count}% OFF',
        dailyRate: 'rates from',
        priceFrom: 'Rates from',
        adult: '1 adult | {count} adults',
        night: '1-night | {count}-nights',
        disclaimer: 'Room rates based on a {nights} stay for {adults}',
        seeHotel: 'See more',
        seeMore: 'See more',
        book: 'Book',
        noResults: 'We did not find results for the selected filters.',
        noResultsSubtitle:
          'Try expanding your search criteria, we have great hotel options for you!',
        searchByHotelName: 'Search by hotel name',
        searchByDestination: 'Search by destination',
        mapViewTitle:
          'Select the hotel from the list </br> or by icon on the map',
        mapViewClose: 'Close map',
        viewOnMap: 'View on map',
      },
      es: {
        discount: 'Hasta {count}% OFF',
        dailyRate: 'a partir de',
        priceFrom: 'A partir de',
        adult: '1 adulto | {count} adultos',
        night: '1 noche | {count} noches',
        disclaimer:
          'Valor de la diaria basado en una busca de {nights} para {adults}',
        seeHotel: 'Ver más',
        seeMore: 'Ver más',
        book: 'Reservar',
        noResults: 'No encontramos resultados para los filtros seleccionados.',
        noResultsSubtitle:
          'Intente expandir sus criterios de búsqueda, tenemos excelentes opciones de hoteles para usted',
        searchByHotelName: 'Buscar por nombre de hotel',
        searchByDestination: 'Buscar por destino',
        mapViewTitle:
          'Seleccione el hotel de la lista </br> o por icono en el mapa',
        mapViewClose: 'Cerrar mapa',
        viewOnMap: 'Ver en el mapa',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/base/mixins.scss';

::v-deep {
  .v-select {
    position: relative;
    width: 100% !important;
    padding-left: 48px !important;

    &:before {
      content: '';
      position: absolute;
      left: 16px;
      width: 24px;
      height: 24px;
      background-image: url('../assets/search.png');
    }

    input {
      color: $gray-90;
    }

    .vs__search {
      font-size: 1em;
    }

    .vs__actions {
      padding: 0;
    }

    .vs__open-indicator {
      display: none;
    }

    .vs__clear {
      padding: 0 4px;
      margin: 0;
    }

    .vs__dropdown-toggle {
      border: none;
      padding: 0;
      background: none;
      width: 100%;

      .vs__selected-options {
        max-width: calc(100% - 23px);

        input {
          margin: 0;
          padding: 0;
        }
      }

      .vs__selected {
        width: calc(100% - 10px);
        display: block;
        margin: 0;
        padding: 0;
        color: $gray-90;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .vs__dropdown-option {
      color: $gray-90;
      font-size: 16px;
      line-height: 1.2;
      white-space: unset;
      padding: 6px 20px;

      &--highlight {
        color: $white;
      }
    }
  }
}

.hotels-list {
  text-align: center;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .tit,
  .sub {
    text-align: center;
  }

  .hotels {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;

    &__controls {
      display: flex;
      justify-content: center;
      margin-bottom: 40px;

      @include media-breakpoint-down(lg) {
        flex-direction: column;
      }

      > * + * {
        margin-left: 24px;
      }

      .control-item {
        display: flex;
        align-items: center;
        width: 100%;
        max-width: 480px;
        height: 56px;
        padding: 8px 16px;
        background: $gray-10;
        border: 1px solid $gray-40;
        border-radius: 12px;
        font-size: 18px;
        color: $gray-90;

        @include media-breakpoint-down(lg) {
          margin-left: 0;

          &:not(:last-child) {
            margin-bottom: 8px;
          }
        }

        @include media-breakpoint-down(sm) {
          height: 48px;
          font-size: 16px;
        }
      }

      .open-map {
        max-width: 200px;
        justify-content: center;
        cursor: pointer;

        @include media-breakpoint-down(md) {
          max-width: 480px;
          justify-content: flex-start;
        }

        &:hover {
          outline: 1px solid $gray-40;
        }

        .icon {
          margin-right: 16px;

          @include media-breakpoint-down(lg) {
            margin-right: 12px;
          }
        }
      }
    }

    &__list {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: -20px;
      margin-bottom: 20px;
      text-align: center;

      &__item {
        box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
        position: relative;
        width: calc(100% / var(--columns-on-desktop, 3) - 40px);
        max-height: 360px;
        margin: 20px;

        &::after {
          content: '';
          display: block;
          padding-bottom: 100%;
        }

        @include media-breakpoint-down(xl) {
          width: calc(100% / 2 - 40px);
        }

        @include media-breakpoint-down(md) {
          width: 100%;
        }
      }
    }
  }

  .bloco-hotel {
    position: relative;
    display: flex;
    align-items: flex-end;
    width: 100%;
    height: 100%;
    transform: scale(1) translateZ(0);
    transition: box-shadow 0.2s ease-out, transform 0.2s ease-out;
    background-position: center;
    background-size: cover;

    &:hover {
      box-shadow: -6px 6px 12px -3px rgba(0, 0, 0, 0.2);
      transform: scale(1.02) translateZ(0);

      .cta {
        &:after {
          transform: scaleX(1);
        }
      }
    }

    .infos {
      width: 100%;
      padding: 16px;
      box-shadow: 0 -140px 20px -20px rgba(0, 0, 0, 0.5) inset;
      text-align: left;

      &-destination {
        margin-bottom: 8px;
      }

      &-content {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      &-wrapper {
        flex-grow: 1;
      }
    }

    .promo {
      position: absolute;
      top: 20px;
      left: 20px;
      padding: 8px 16px;
      background-color: var(--promo-color);
      border-radius: 100px;
      line-height: 1;
      color: $white;
      font-size: $size-normal;
      font-weight: 500;
      text-transform: uppercase;
    }

    .cidade,
    .nome,
    .diaria {
      font-size: $size-normal;
      color: $white;
    }

    .cidade {
      display: inline-block;
      line-height: 24px;
      margin: 0;
      margin-bottom: 10px;
      padding: 0 16px;
      background: #fff5f5;
      border-radius: 100px;
      color: $gray-90;
      font-weight: 500;
    }

    .nome {
      margin: 0;
      margin-bottom: 2px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .valor {
      position: relative;
      border-bottom: 1px solid;
      font-weight: 700;
      color: var(--primary-color);

      &.has-discount {
        color: var(--promo-color);
      }

      &:hover {
        .slb-tooltip {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }

      .slb-tooltip {
        visibility: hidden;
        opacity: 0;
      }
    }

    .valor-promo {
      text-decoration: line-through;
    }

    .cta {
      position: relative;
      width: 120px;
      flex-shrink: 0;
      padding: 0;
      font-size: $size-normal;
      font-weight: 400;
      white-space: nowrap;
      text-decoration: none;
    }

    @include media-breakpoint-down(md) {
      box-shadow: none;
      margin-bottom: res(20);
      width: 100%;

      .promo {
        font-size: res(12);
        padding: res(5) res(10);
      }

      .nome,
      .diaria,
      .cidade {
        font-size: res(12);
      }

      .cta {
        width: res(100);
        font-size: res(12);
      }
    }
  }

  .cta--ver-mais {
    width: 200px;
    margin: 0 auto;

    @include media-breakpoint-down(md) {
      width: 100%;
    }
  }

  .no-hotels {
    padding: 60px;
    text-align: left;
    transform-origin: 50% 100%;
    background-color: var(--secondary-color);

    @include media-breakpoint-down(sm) {
      padding: res(30);
    }

    .tit {
      font-size: 30px;
      color: $white;

      @include media-breakpoint-down(md) {
        font-size: res(22);
        line-height: 1.3em;
        margin-bottom: 1em;
      }
    }

    &__message {
      font-weight: 300;
      position: relative;
      z-index: 2;
      color: $white;
      text-align: center;

      @include media-breakpoint-down(md) {
        font-size: res(14);
      }
    }
  }

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  .map-modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    z-index: 999999999; // sobrepor chatbots

    .close-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 999;
      min-width: 42px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 16px;
      background: $white;
      border: 1px solid $gray-40;
      border-radius: 8px;
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.07);
      color: $danger;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        background: $danger-20;
      }

      .icon {
        --size: 20px;
        margin-right: 4px;
      }

      @include media-breakpoint-down(sm) {
        padding: 10px;

        span {
          display: none;
        }
        .icon {
          --size: 24px;
          margin-right: 0;
        }
      }
    }

    .map-hotels {
      width: 360px;
      flex-shrink: 0;
      background: $white;
      padding: 32px;
      text-align: left;
      z-index: 999; // para a sombra sobrepor o mapa
      box-shadow: 4px 4px 25px 0px rgba(0, 0, 0, 0.25);

      @include media-breakpoint-down(lg) {
        display: none;
      }

      &__header {
        color: $black;
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -1px;
        line-height: 1.3;
        margin-bottom: 32px;
      }
    }

    .hotel-card {
      width: 100%;
      background-position: center;
      background-size: cover;
      border-radius: 12px;
      margin-bottom: 16px;
      overflow: hidden;
      color: $white;
      text-align: left;

      &[on-focus] {
        transform: translateY(-3px);
        box-shadow: 0px 10px 16px -4px rgba(0, 0, 0, 0.6);
      }

      &__content {
        display: flex;
        flex-direction: column;
        min-height: 130px;
        background: rgba(0, 0, 0, 0.3);
        padding: 16px;
      }

      &__name {
        font-size: 14px;
        font-weight: 700;
        margin-bottom: auto;
      }

      &__cta {
        margin-top: 8px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 100px;
        font-size: 12px;
        color: inherit;
        text-align: center;
        line-height: 1;
        cursor: pointer;

        &--no-price {
          width: 50px;
          align-self: flex-end;
        }
      }

      &__price {
        vertical-align: middle;

        strong {
          color: $secondary;
        }
      }

      &--floating {
        // card flutuante
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9999;
        width: 90%;
        max-width: 320px;
        margin: 0;
        border: none;
        box-shadow: 0px -8px 10px 0px rgba(0, 0, 0, 0.2);
      }
    }

    .map-container {
      width: 100%;
      position: relative;
    }

    .map-search {
      position: absolute;
      top: 8px;
      right: calc((100% - 44px - 151px) / 2 + 151px);
      transform: translateX(50%);
      width: calc(100% - 44px - 151px - 32px);
      max-width: 500px;
      z-index: 999;
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.07);

      @include media-breakpoint-down(sm) {
        right: calc((100% - 46px) / 2 + 46px);
        width: calc(100% - 46px - 32px);
      }

      .v-select {
        display: flex;
        align-items: center;
        height: 46px;
        padding: 4px 8px;
        background: $white;
        border: 1px solid $gray-40;
        border-radius: 8px;
        font-size: 16px;

        @include media-breakpoint-down(sm) {
          padding-left: 36px !important;

          &::before {
            left: 10px;
          }
        }
      }
    }

    .vue2leaflet-map {
      width: 100%;
      height: 100%;
      font-family: Roboto, Arial, sans-serif;

      ::v-deep {
        @include media-breakpoint-down(lg) {
          .leaflet-tooltip {
            display: none;
          }
        }
        @include media-breakpoint-down(sm) {
          .leaflet-control-container .leaflet-top.leaflet-left {
            display: none;
          }
        }
      }
    }

    .pin {
      &__tooltip {
        display: flex;
        flex-direction: column;
        width: 100px;
        gap: 8px;
        text-align: left;
        white-space: wrap;
      }

      &__name {
        font-size: 14px;
      }

      &__price strong {
        color: $secondary;
      }
    }

    ::v-deep {
      .pin__tag {
        // as dimensões finais do pin são definidas no template
        height: 24px;
        line-height: 22px;
        background: var(--marker-color, $primary-140);
        color: $white;
        border-radius: 8px;
        border: 1px solid var(--marker-color);

        &::after {
          --size: 8px;
          content: '';
          display: block;
          width: 0;
          height: 0;
          position: absolute;
          top: calc(100% - 3px);
          left: 50%;
          transform: translateX(-50%);
          border-left: var(--size) solid transparent;
          border-right: var(--size) solid transparent;
          border-top: var(--size) solid var(--marker-color, $primary-140);
        }

        &:hover,
        &--selected {
          background: $secondary;

          &::after {
            border-top-color: $secondary;
          }
        }
      }

      .pin__icon {
        --color: var(--marker-color, #{$primary-140}) !important;

        &:hover,
        &--selected {
          --color: #{$secondary} !important;
        }
      }
    }
  }
}
</style>
